
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSession } from '@supabase/auth-helpers-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FunctionsHttpError } from '@supabase/supabase-js';
import Recorder from 'recorder-core';
import 'recorder-core/src/engine/wav.js';

import { supabase } from '../supabaseClient';
import {
  getCourse,
  getCourseStatus,
  createCourseStatus,
  updateCourseStatus,
  triggerCourseContentGeneration,
  updateUserProgressSummary,
  getFullUserProfile,
  awardUserXp,
} from '../api/supabaseAPI';
import { getCorrectRateFromLog, getErrorRateFromLog, getSessionCreatedAt } from '../utils/practiceSession';
import ConfirmDialog from '../components/ConfirmDialog';
import DiagnosisReport from '../components/DiagnosisReport';
import PracticeReportModal from '../components/PracticeReportModal';
import AudioRecorderControls from '../components/AudioRecorderControls';
import AlertOverlay from '../components/AlertOverlay';
import '../styles/Practice.css';
import '../styles/Layout.css';
import '../styles/InitialTest.css'; // Reusing InitialTest styles for consistency

export default function CoursePractice() {
  const { cid } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const session = useSession();
  const queryClient = useQueryClient();
  const userId = session?.user?.id;
  const courseId = parseInt(cid, 10);

  // --- Local State ---
  const [isRecording, setIsRecording] = useState(false);
  const [timer, setTimer] = useState(0);
const [isConfirmingNext, setIsConfirmingNext] = useState(false);
  const [mediaError, setMediaError] = useState(null);
  const [isAcquiringMedia, setIsAcquiringMedia] = useState(false);
  const [isGeneratingContent, setIsGeneratingContent] = useState(false);
  const [localAnalysisResult, setLocalAnalysisResult] = useState(null); // ✨ NEW: Local analysis result
  const [isWaitingForAnalysis, setIsWaitingForAnalysis] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');

  const recorderRef = useRef(null);
  const recordingStreamRef = useRef(null);

  // --- Queries ---
  const { data: userProfile } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => getFullUserProfile(userId),
    enabled: !!userId,
  });

  const { data: course, isLoading: isLoadingCourse, isError: isCourseError } = useQuery({
    queryKey: ['course', courseId],
    queryFn: () => getCourse(courseId),
    enabled: !!courseId,
    // Poll if content is generating
    refetchInterval: (data) => {
        const content = data?.course_contents?.content;
        // If content is missing or empty, poll every 2 seconds
        return (!content || content.length === 0) ? 2000 : false;
    }
  });

  const { data: courseStatus, isLoading: isLoadingStatus, refetch: refetchStatus } = useQuery({
    queryKey: ['courseStatus', courseId],
    queryFn: () => getCourseStatus(courseId),
    enabled: !!courseId,
    // ✨ Fallback Polling: Check for report every 2s if waiting (in case Realtime fails)
    refetchInterval: (data) => {
        if (isWaitingForAnalysis) return 2000;
        return false;
    }
  });

  // --- Derived State ---
  const courseContentList = useMemo(() => course?.course_contents?.content || [], [course]);
  const isContentReady = courseContentList.length > 0;
  
  const currentIndex = useMemo(() => {
    if (!courseStatus?.cur_lvl) return 0;
    // Format: "index_N"
    const parts = courseStatus.cur_lvl.split('_');
    if (parts.length === 2 && parts[0] === 'index') {
      return parseInt(parts[1], 10);
    }
    return 0;
  }, [courseStatus]);

  const currentSentence = useMemo(() => {
    return courseContentList[currentIndex] || null;
  }, [courseContentList, currentIndex]);

  // ✨ Modified: Check local state first, then DB state
  const currentLog = localAnalysisResult || courseStatus?.cur_log;
  const hasDiagnosisLog = !!currentLog;
  const hasAnalysisReport = useMemo(() => !!courseStatus?.cur_report && typeof courseStatus.cur_report === 'object', [courseStatus]);
  const hasValidAnalysisReport = useMemo(() => {
    return hasAnalysisReport && courseStatus.cur_report.error === undefined;
  }, [hasAnalysisReport, courseStatus]);
  const isCompleted = courseStatus?.is_completed;

  // --- Effects ---

  // ✨ Fail-safe: Clear waiting state if report appears via Polling
  useEffect(() => {
      if (isWaitingForAnalysis && hasAnalysisReport) {
          console.log("Polling/Realtime detected report. Clearing wait state.");
          setIsWaitingForAnalysis(false);
      }
  }, [isWaitingForAnalysis, hasAnalysisReport]);

  // ✨ Realtime Subscription for Background Analysis
  useEffect(() => {
    if (!userId || !courseId) return;

    const channel = supabase
      .channel(`course_practice_realtime_${courseId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'course_status',
          filter: `cid=eq.${courseId}`,
        },
        (payload) => {
           console.log("Realtime payload received:", payload); // Debug log
           // Handle Analysis Report Generation
           if (payload.new && payload.new.cur_report) {
               console.log("Realtime update: New Course Report Generated", payload.new.cur_report);
               queryClient.invalidateQueries({ queryKey: ['courseStatus', courseId] });
               // Clear waiting state after a short delay to ensure UI updates smoothly
               setTimeout(() => setIsWaitingForAnalysis(false), 500);
           }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_settings',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
           if (payload.new.error_status) {
              console.error("Realtime error status received:", payload.new.error_status);
              // Stop all loading states
              setIsWaitingForAnalysis(false);
              setIsGeneratingContent(false);
           }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, courseId, queryClient]);

  // 1. Check if content generation is needed
  useEffect(() => {
    if (course && !isLoadingCourse && !isContentReady && !isGeneratingContent) {
      console.log("Course content missing. Triggering generation...");
      setIsGeneratingContent(true);
      triggerCourseContentGeneration(courseId)
        .then(() => console.log("Generation triggered."))
        .catch(err => {
            console.error("Failed to trigger generation:", err);
            setIsGeneratingContent(false);
        });
    }
    
    // Stop generating flag if content appears
    if (isContentReady && isGeneratingContent) {
        setIsGeneratingContent(false);
    }
  }, [course, isLoadingCourse, isContentReady, isGeneratingContent, courseId]);

  // 2. Initialize Status if missing
  useEffect(() => {
    if (courseId && !isLoadingStatus && !courseStatus && isContentReady) {
       console.log("Status missing. Creating initial status...");
       createCourseStatus(courseId)
         .then(() => {
             queryClient.invalidateQueries(['courseStatus', courseId]);
         })
         .catch(err => console.error("Failed to create status:", err));
    }
  }, [courseId, isLoadingStatus, courseStatus, isContentReady, queryClient]);

  // 3. Timer
  useEffect(() => {
    let intervalId;
    if (isRecording) {
      intervalId = setInterval(() => {
        setTimer(prevTimer => prevTimer + 1);
      }, 1000);
    }
    return () => clearInterval(intervalId);
  }, [isRecording]);


  // --- Mutations ---

  const { mutate: analyzeRecording, isPending: isAnalyzingRecording } = useMutation({
    mutationFn: async ({ audioBlob, text, lang }) => {
        const formData = new FormData();
        formData.append('audio', audioBlob, 'recording.wav');
        formData.append('target_content', text);
        formData.append('language', lang);
        
        const { data, error } = await supabase.functions.invoke('analyze-speech', { body: formData });
        if (error) {
            if (error instanceof FunctionsHttpError) {
                const errorJson = await error.context.json();
                throw new Error(errorJson.error || error.message);
            }
            throw error;
        }
        return data;
    },
    onSuccess: (data) => {
        // Prepare Error Stats (copied logic from InitialTest)
        const phonemeStats = {};
        (data.words || [{ phonemes: data.phonemes || [] }])
        .flatMap(word => word.phonemes || [])
        .forEach(p => {
          if (p.target === '-') return;
          const targetPhoneme = p.target;
          if (!phonemeStats[targetPhoneme]) phonemeStats[targetPhoneme] = {};
          if (p.isMatch) {
            phonemeStats[targetPhoneme]['correct'] = (phonemeStats[targetPhoneme]['correct'] || 0) + 1;
          } else {
            const errorType = (p.user === '-') ? 'omission' : 'substitution';
            phonemeStats[targetPhoneme][errorType] = (phonemeStats[targetPhoneme][errorType] || 0) + 1;
          }
        });

        const statsToUpload = Object.entries(phonemeStats).flatMap(([phoneme, events]) => 
            Object.entries(events).map(([event_type, count]) => ({
              phoneme,
              event_type,
              count
            }))
        );

        // ✨ Attach stats to data object for local use
        data.calculatedStats = statsToUpload;
        
        // ✨ Only update local state
        setLocalAnalysisResult(data);

        // ✨ RESTORE: Update DB with current analysis so it persists on reload (Draft state)
        updateCourseStatus(courseId, {
            cur_log: data,
            cur_err: statsToUpload
        }).then(() => {
             queryClient.invalidateQueries(['courseStatus', courseId]);
        });
    },
    onError: (error) => {
        console.error("Analysis failed:", error);
        alert(`Analysis failed: ${error.message}`);
    }
  });

  const { mutate: analyzeReport, isPending: isGeneratingReport } = useMutation({
    mutationFn: async () => {
      // ✨ Start waiting state immediately
      setIsWaitingForAnalysis(true);
      
      const { data, error } = await supabase.functions.invoke('analyze-practice-session', {
        body: { 
            language: course.language,
            course_id: courseId // ✨ Pass course_id to use polymorphic function
        },
      });

      if (error) {
        if (error instanceof FunctionsHttpError) {
          const errorJson = await error.context.json();
          throw new Error(`Analysis report generation failed: ${errorJson.error || error.message}`);
        }
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      // Wait for Realtime to clear waiting state
      queryClient.invalidateQueries({ queryKey: ['courseStatus', courseId] });
      console.log("Report generation initiated.");
    },
    onError: (error) => {
      setIsWaitingForAnalysis(false);
      queryClient.invalidateQueries({ queryKey: ['courseStatus', courseId] });
      alert(error.message);
    },
  });

  const { mutate: advanceNext, isPending: isAdvancing } = useMutation({
      mutationFn: async ({ isSkip }) => {
          const nextIndex = currentIndex + 1;
          const isFinished = nextIndex >= courseContentList.length;

          // 1. Save Session Record
          // Use localAnalysisResult if available, otherwise courseStatus.cur_log
          const logToStore = localAnalysisResult || courseStatus?.cur_log || { status: 'skipped' };
          const errorRate = getErrorRateFromLog(logToStore, isSkip);
          const correctRate = getCorrectRateFromLog(logToStore, isSkip);
          
          // Use calculated stats from local result if available
          const errorStats = localAnalysisResult?.calculatedStats || courseStatus?.cur_err || null;

          // ✨ Prepare created_at timestamp
          const createdAt = getSessionCreatedAt(logToStore, isSkip);

          await supabase.from('practice_sessions').insert([{
              user_id: userId,
              language: course.language,
              target_content: currentSentence,
              error_rate: errorRate,
              full_log: logToStore,
              diffi_level: `Course${courseId}_${currentIndex + 1}`, // ✅ Format: Course{id}_{sentenceNumber}
              practice_error: errorStats,
              full_report: courseStatus?.cur_report || null, // ✅ Include AI analysis report
              created_at: createdAt, // ✨ Include explicit timestamp
          }]);
          await awardUserXp(userId, 'course_practice_session', correctRate);

          // 2. Update Global Progress (if not skipped and has errors)
          if (!isSkip && errorStats?.length > 0) {
              await updateUserProgressSummary(userId, course.language, errorStats);
          }

          // 3. Update Course Status
          const updates = {
              cur_lvl: `index_${nextIndex}`,
              cur_content: courseContentList[nextIndex] || null, // Next content or null if finished
              cur_log: null,
              cur_err: null,
              cur_report: null,
              is_completed: isFinished,
              completed_at: isFinished ? new Date().toISOString() : null
          };
          
          await updateCourseStatus(courseId, updates);
          return isFinished;
      },
      onSuccess: (isFinished) => {
          setLocalAnalysisResult(null); // Clear local state
          queryClient.invalidateQueries(['courseStatus', courseId]);
          queryClient.invalidateQueries(['user', userId]); // Update profile stats
          if (isFinished) {
              // Maybe show a completion modal? For now just stay on page or navigate
              alert("Course Completed! Congratulations!");
              navigate('/courses');
          }
      },
      onError: (err) => {
          console.error("Failed to advance:", err);
          alert("Failed to save progress.");
      }
  });


  // --- Handlers ---

  const stopRecordingStream = () => {
    if (recordingStreamRef.current) {
      recordingStreamRef.current.getTracks().forEach((track) => track.stop());
      recordingStreamRef.current = null;
    }
  };

  const handleRecordToggle = async () => {
    if (recorderRef.current && isRecording) {
      recorderRef.current.stop(
        (blob) => {
          setIsRecording(false);
          setTimer(0);
          analyzeRecording({ audioBlob: blob, text: currentSentence, lang: course.language });
          recorderRef.current.close();
          recorderRef.current = null;
          stopRecordingStream();
        },
        (err) => {
          console.error("Stop recording failed:", err);
          setMediaError(`Recording error: ${err}`);
          setIsRecording(false);
          stopRecordingStream();
        }
      );
    } else {
      setMediaError(null);
      let selectedStream = null;
      if (selectedDeviceId) {
        try {
          selectedStream = await navigator.mediaDevices.getUserMedia({
            audio: { deviceId: { exact: selectedDeviceId } },
          });
          recordingStreamRef.current = selectedStream;
        } catch (err) {
          setMediaError(err instanceof Error ? err.message : 'Microphone access failed.');
          return;
        }
      }

      const rec = Recorder({
        type: 'wav',
        sampleRate: 16000,
        bitRate: 16,
        sourceStream: selectedStream || undefined,
      });
      setIsAcquiringMedia(true);
      rec.open(
        () => {
          setIsAcquiringMedia(false);
          rec.start();
          recorderRef.current = rec;
          setIsRecording(true);
          setTimer(0);
        },
        (err, isUserNotAllow) => {
          setIsAcquiringMedia(false);
          setMediaError(isUserNotAllow ? "Microphone access was denied." : `Could not open recorder: ${err}`);
          stopRecordingStream();
        }
      );
    }
  };

  const handleDeleteRecording = () => {
    if (recorderRef.current && isRecording) {
      recorderRef.current.stop(
        () => {
          setIsRecording(false);
          setTimer(0);
          recorderRef.current.close();
          recorderRef.current = null;
          stopRecordingStream();
        },
        (err) => {
          console.error("Stop recording failed:", err);
          setMediaError(`Recording error: ${err}`);
          setIsRecording(false);
          stopRecordingStream();
        }
      );
    }
  };

  const handleTryAgain = () => {
      // 1. Clear local state
      setLocalAnalysisResult(null);

      // 2. Optimistic update for React Query cache
      queryClient.setQueryData(['courseStatus', courseId], old => ({
          ...old,
          cur_log: null,
          cur_err: null,
          cur_report: null // Clear report too
      }));

      // 3. Clear DB state so it doesn't persist or reappear on reload
      updateCourseStatus(courseId, { cur_log: null, cur_err: null, cur_report: null })
        .catch(err => console.error("Failed to clear DB on Try Again:", err));
  };

  const handleAnalysis = () => {
    if (hasAnalysisReport) {
      if (hasValidAnalysisReport) {
        setIsReportModalOpen(true);
      } else {
        alert(courseStatus.cur_report.error || "AI analysis failed. Please try again.");
      }
    } else if (!isGeneratingReport) {
      analyzeReport();
    }
  };

  const handleNext = () => {
      advanceNext({ isSkip: false });
  };

  const handleNextClick = () => {
    if (hasValidAnalysisReport) {
      // Report already generated, proceed directly
      handleNext();
    } else {
      // No report, show confirmation dialog
      setIsConfirmingNext(true);
    }
  };


  // --- Render ---

  if (isLoadingCourse || (isContentReady && isLoadingStatus)) {
      return <div className="spinner-container"><div className="spinner"></div></div>;
  }

  if (isCourseError || !course) {
      return <div className="error-container">Course not found.</div>;
  }

  if (!isContentReady) {
      return (
        <main className="main-content width-intro" style={{ textAlign: 'center' }}>
            <div className="spinner" style={{ margin: '2rem auto' }}></div>
            <h2>Generating Course Content...</h2>
            <p>This course is being prepared for you. This may take a few moments.</p>
        </main>
      );
  }

  if (isCompleted) {
      return (
          <main className="main-content width-practice practice-page-shell course-practice-page">
              <div className="difficulty-section">
                  <h2 className="section-title">Course Completed!</h2>
                  <p>You have finished <b>{course.course_name}</b>.</p>
                  <button className="practice-btn primary" onClick={() => navigate('/courses')}>
                      Back to Courses
                  </button>
              </div>
          </main>
      );
  }

  const formatTime = (seconds) => `${Math.floor(seconds / 60).toString().padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`;
  const isProcessing = isAnalyzingRecording || isAdvancing || isGeneratingReport || isWaitingForAnalysis;

  return (
    <>
      <main className="main-content width-practice practice-page-shell course-practice-page">
        <div className="difficulty-section">
            <h3 className="section-title">
                {course.course_name} ({currentIndex + 1} / {courseContentList.length})
            </h3>
        </div>

        <div className="practice-area">
             <p className="practice-text">{currentSentence || "Loading..."}</p>
             
             <div className="practice-controls">
                {hasDiagnosisLog && (
                    <button
                        className="practice-btn"
                        onClick={handleTryAgain}
                        disabled={isProcessing}
                    >
                        Try Again
                    </button>
                )}
             </div>
        </div>

        {mediaError && <div className="media-error-alert">{mediaError}</div>}
        {isAcquiringMedia && <div className="media-info-alert">Accessing microphone...</div>}

        {hasDiagnosisLog && (() => {
            const summary = currentLog?.summary;
            const phonemeErrorRate = summary?.phonemeErrorRate ?? 100.0;
            const correctRate = 100 - phonemeErrorRate;
            const isRateTooLow = correctRate < 40;
            
            return (
                <div>
                    {isRateTooLow && (
                        <p className="rate-warning-text">
                            Correct rate is {correctRate.toFixed(0)}% (minimum 40% required). Please "Try Again".
                        </p>
                    )}
                    <DiagnosisReport
                        result={currentLog}
                        nextAction={
                            <>
                                <button 
                                  className="practice-btn secondary"
                                  onClick={handleAnalysis}
                                  disabled={isGeneratingReport || isWaitingForAnalysis || isRateTooLow || (hasAnalysisReport && !hasValidAnalysisReport)} 
                                  style={{ marginBottom: '0.75rem' }}
                                >
                                  {(() => {
                                    if (isGeneratingReport || isWaitingForAnalysis) {
                                      return 'Analyzing ...';
                                    }
                                    if (hasValidAnalysisReport) {
                                      return 'View Report';
                                    }
                                    if (hasAnalysisReport) {
                                      return 'Analysis Failed'; 
                                    }
                                    return 'Analysis';
                                  })()}
                                </button>
                                <button
                                    className="practice-btn primary"
                                    onClick={handleNextClick}
                                    disabled={isAdvancing || isRateTooLow}
                                >
                                    {isAdvancing ? 'Saving...' : 'Next →'}
                                </button>
                            </>
                        }
                    />
                </div>
            );
        })()}

      </main>

      <AudioRecorderControls
        isRecording={isRecording}
        recordDisabled={isProcessing || hasDiagnosisLog || isAcquiringMedia}
        deleteDisabled={!isRecording}
        timerText={formatTime(timer)}
        onRecordToggle={handleRecordToggle}
        onDelete={handleDeleteRecording}
        selectedDeviceId={selectedDeviceId}
        onDeviceChange={setSelectedDeviceId}
        micDisabled={isProcessing || hasDiagnosisLog || isAcquiringMedia || isRecording}
      />

      {(isAnalyzingRecording || isGeneratingReport || isWaitingForAnalysis) && (
        <AlertOverlay
          message={(isGeneratingReport || isWaitingForAnalysis) ? 'Analyzing Report ...' : 'Analyzing...'}
        />
      )}

      <ConfirmDialog
        isOpen={isConfirmingNext}
        title="Skip Report Generation?"
        message="You haven't generated a detailed report for this sentence. Once you proceed, you won't be able to generate it. Continue anyway?"
        onConfirm={() => {
          setIsConfirmingNext(false);
          handleNext();
        }}
        onCancel={() => setIsConfirmingNext(false)}
      />
      <PracticeReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        report={courseStatus?.cur_report}
      />
    </>
  );
}
